import { useState } from "react";
import styled from "@emotion/styled";
import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextOverflow,
  MDSTextVariant,
} from "@/mds";
import type { FC, ReactNode } from "react";

export interface CollapsibleSectionHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: string | null;
  children: ReactNode;
  defaultCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
}

export const CollapsibleSectionHeader: FC<CollapsibleSectionHeaderProps> = ({
  title,
  children,
  defaultCollapsed = false,
  onCollapseChange,
  className,
  ...props
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isHovered, setIsHovered] = useState(false);

  const sectionTitle = title ?? "Section";

  const toggle = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    onCollapseChange?.(next);
  };

  return (
    <>
      <SectionHeaderContainer
        {...props}
        className={className}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={toggle}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
      >
        <SectionHeaderButton $isHovered={isHovered}>
          <SectionHeaderIcon $isCollapsed={isCollapsed}>
            <MDSIcon
              kind={isCollapsed ? MDSIconKind.CaretRight : MDSIconKind.CaretDown}
              color={MDSIconColor.Secondary}
              size={11}
            />
          </SectionHeaderIcon>
        </SectionHeaderButton>
        <SectionHeaderTitle variant={MDSTextVariant.TextXs} overflow={MDSTextOverflow.Regular}>
          {sectionTitle}
        </SectionHeaderTitle>
      </SectionHeaderContainer>
      {!isCollapsed && children}
    </>
  );
};

const SectionHeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding-top: ${({ theme }) => theme.spacing["2"]};
  padding-bottom: ${({ theme }) => theme.spacing["2"]};
  padding-left: ${({ theme }) => theme.spacing["2"]};
  padding-right: ${({ theme }) => theme.spacing["2"]};
  min-height: 32px;
  align-self: stretch;
  width: 100%;
  cursor: pointer;
`;

const SectionHeaderButton = styled.button<{ $isHovered: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: ${({ theme, $isHovered }) =>
    $isHovered
      ? theme.colors.background.button.transparent.hover
      : theme.colors.background.button.transparent.default};
  border: none;
  cursor: pointer;
  border-radius: 100px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }

  & svg {
    margin-top: -1px;
  }
`;

const SectionHeaderIcon = styled.div<{ $isCollapsed: boolean }>`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 150ms ease;
`;

const SectionHeaderTitle = styled(MDSText)`
  text-transform: uppercase;
  letter-spacing: 0.24px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  flex: 1;
  text-align: left;
  max-width: calc(100% - 48px);
`;
